import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Divider,
  Box,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import PrintIcon from "@material-ui/icons/Print";
import KOTPrint from "./KOTPrint";
import * as api from "../api/index";

function groupByBundle(items) {
  if (!items || !items.length) return { unprinted: [], bundles: [] };
  const unprinted = items.filter((i) => !i.kotPrinted);
  const printed = items.filter((i) => i.kotPrinted && i.kotBundleNumber != null);
  const byBundle = {};
  printed.forEach((i) => {
    const n = i.kotBundleNumber;
    if (!byBundle[n]) byBundle[n] = [];
    byBundle[n].push(i);
  });
  const bundles = Object.entries(byBundle)
    .map(([num, list]) => ({ bundleNumber: Number(num), items: list }))
    .sort((a, b) => a.bundleNumber - b.bundleNumber);
  return { unprinted, bundles };
}

function KOTDialog({ open, onClose, session, onSuccess, onError, refreshSession }) {
  const [sending, setSending] = useState(false);
  const [reprinting, setReprinting] = useState(null);
  const [showKOTPrint, setShowKOTPrint] = useState(false);
  const [printPayload, setPrintPayload] = useState(null);
  const { unprinted, bundles } = useMemo(() => {
    const items = session?.items || [];
    if (open && items.length === 0 && session?._id) {
      console.warn("[KOTDialog] Session has no items:", session);
    }
    return groupByBundle(items);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.items, open]);

  const nextBundleNumber = bundles.length > 0
    ? Math.max(...bundles.map((b) => b.bundleNumber)) + 1
    : 1;

  useEffect(() => {
    if (!open) {
      setPrintPayload(null);
      setShowKOTPrint(false);
      setReprinting(null);
    } else if (session?._id && refreshSession) {
      refreshSession();
    }
  }, [open]);


  const handleSendToKitchen = async () => {
    if (!unprinted.length) {
      const errorMsg = "No new items to send";
      onError?.(errorMsg);
      return;
    }

    setSending(true);
    try {
      const itemIds = unprinted.map((item) => item.itemId?._id || item.itemId);
      const { data } = await api.printKot(session._id, itemIds);
      const newBundleNumber = data.kotBundleNumber ?? nextBundleNumber;

      setPrintPayload({
        items: unprinted.map((i) => ({ name: i.name, qty: i.qty, remark: i.remark })),
        kotNumber: newBundleNumber,
      });
      setShowKOTPrint(true);

      if (refreshSession) await refreshSession();

      setTimeout(() => {
        setShowKOTPrint(false);
        setPrintPayload(null);
        const message = `KOT #${newBundleNumber} sent to kitchen (${unprinted.length} items)`;
        onSuccess?.(message);
      }, 3500);
    } catch (err) {
      console.error(err);
      setShowKOTPrint(false);
      setPrintPayload(null);
      const errorMsg = err?.response?.data?.message || "Failed to send KOT";
      onError?.(errorMsg);
    } finally {
      setSending(false);
    }
  };

  const handleReprintBundle = (bundle) => {
    setReprinting(bundle.bundleNumber);
    const items = bundle.items.map((i) => ({ name: i.name, qty: i.qty, remark: i.remark }));
    setPrintPayload({ items, kotNumber: bundle.bundleNumber });
    setShowKOTPrint(true);
    setTimeout(() => {
      setShowKOTPrint(false);
      setPrintPayload(null);
      setReprinting(null);
    }, 3500);
  };

  const handlePrintComplete = () => {
    setShowKOTPrint(false);
    setPrintPayload(null);
    setReprinting(null);
  };

  const hasAnyContent = (session?.items?.length || 0) > 0;
  const hasUnprinted = unprinted.length > 0;
  const hasBundles = bundles.length > 0;

  // Debug: Log session data when dialog opens
  useEffect(() => {
    if (open && session) {
      console.log("[KOTDialog] Session data:", {
        id: session._id,
        label: session.label,
        itemsCount: session.items?.length || 0,
        items: session.items,
        unprinted: unprinted.length,
        bundles: bundles.length,
      });
    }
  }, [open, session, unprinted.length, bundles.length]);

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="sm" 
        fullWidth
        style={{ zIndex: 10001 }}
        PaperProps={{ style: { zIndex: 10001 } }}
      >
        <DialogTitle style={{ padding: "10px 14px", borderBottom: "1px solid #e0e0e0" }}>
          {/* First row: Title and Close icon */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
            <Typography variant="h6" style={{ fontSize: "16px", fontWeight: 600 }}>
              Kitchen Order - {session?.label || "—"}
            </Typography>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <IconButton 
                size="small" 
                onClick={onClose} 
                disabled={sending || reprinting !== null}
                style={{ 
                  color: "#666",
                  padding: 4,
                  width: "28px",
                  height: "28px",
                }}
                title="Close"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </div>
          </div>
          {/* Second row: Subtitle */}
          <Typography variant="caption" color="textSecondary" style={{ fontSize: "10px" }}>
            Send by bundle or reprint any KOT
          </Typography>
        </DialogTitle>
        <DialogContent style={{ 
          padding: "12px 14px",
          overflowY: "auto",
          maxHeight: "calc(100vh - 200px)",
        }}
        className="slim-scrollbar"
        >
          {!session ? (
            <Box py={2} textAlign="center">
              <CircularProgress size={24} />
              <Typography color="textSecondary" style={{ marginTop: 8, fontSize: "13px" }}>Loading session...</Typography>
            </Box>
          ) : !hasAnyContent ? (
            <Box py={2} textAlign="center">
              <Typography color="textSecondary" style={{ fontSize: "13px" }}>No items in this session.</Typography>
            </Box>
          ) : (
            <>
              {/* New items - Send to Kitchen */}
              {hasUnprinted && (
                <Box mb={1.5}>
                  <Typography variant="subtitle2" color="primary" style={{ fontWeight: 600, marginBottom: 3, fontSize: "12px" }}>
                    ✨ New items (auto-saved, not yet sent to kitchen)
                  </Typography>
                  <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 6, fontSize: "10px" }}>
                    These items will create a NEW (KOT #{nextBundleNumber}) when you click "Send to Kitchen"
                  </Typography>
                  <Table size="small" style={{ marginBottom: 8 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="dense" style={{ fontSize: "12px", fontWeight: 600, padding: "6px 8px" }}>Item</TableCell>
                        <TableCell align="center" padding="dense" style={{ fontSize: "12px", fontWeight: 600, padding: "6px 8px" }}>Qty</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {unprinted.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell padding="dense" style={{ fontSize: "12px", padding: "6px 8px" }}>
                            {item.name}
                            {item.remark && (
                              <Typography
                                variant="caption"
                                style={{ display: "block", fontStyle: "italic", color: "#666", marginTop: "2px" }}
                              >
                                Remark: {item.remark}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center" padding="dense" style={{ fontSize: "12px", fontWeight: 600, padding: "6px 8px" }}>{item.qty}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    fullWidth
                    startIcon={sending ? <CircularProgress size={12} /> : <PrintIcon />}
                    onClick={handleSendToKitchen}
                    disabled={sending}
                    style={{ backgroundColor: "#ff9800", fontSize: "11px", padding: "5px 10px", height: "32px" }}
                  >
                    {sending ? "Sending…" : `Send to Kitchen (${unprinted.length} items)`}
                  </Button>
                </Box>
              )}

              {hasUnprinted && hasBundles && <Divider style={{ margin: "10px 0" }} />}

              {/* Sent bundles - Reprint per bundle or all */}
              {hasBundles && (
                <Box>
                  <Typography variant="subtitle2" color="textSecondary" style={{ fontWeight: 600, marginBottom: 3, fontSize: "12px" }}>
                    ✓ Sent to kitchen - Can reprint anytime
                  </Typography>
              
                  {bundles.map((bundle) => (
                    <Box key={bundle.bundleNumber} mb={1} pb={1} style={{ borderBottom: "1px solid #eee" }}>
                      <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 3, fontSize: "11px" }}>
                        KOT #{bundle.bundleNumber} ({bundle.items.length} items)
                      </Typography>
                      <Table size="small" style={{ marginBottom: 4 }}>
                        <TableHead>
                          <TableRow>
                            <TableCell padding="dense" style={{ fontSize: "11px", fontWeight: 600, padding: "4px 6px" }}>Item</TableCell>
                            <TableCell align="center" padding="dense" style={{ fontSize: "11px", fontWeight: 600, padding: "4px 6px" }}>Qty</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {bundle.items.map((item, idx) => (
                            <TableRow key={idx}>
                              <TableCell padding="dense" style={{ fontSize: "11px", padding: "4px 6px" }}>
                                {item.name}
                                {item.remark && (
                              <Typography variant="caption" style={{ display: "block", fontStyle: "italic", color: "#666", marginTop: "2px" }}>
                                    Remark: {item.remark}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell align="center" padding="dense" style={{ fontSize: "11px", padding: "4px 6px" }}>{item.qty}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={reprinting === bundle.bundleNumber ? <CircularProgress size={10} /> : <PrintIcon style={{ fontSize: 12 }} />}
                        onClick={() => handleReprintBundle(bundle)}
                        disabled={reprinting !== null}
                        style={{ marginTop: 3, fontSize: "10px", padding: "3px 8px", height: "26px" }}
                      >
                        Reprint KOT #{bundle.bundleNumber}
                      </Button>
                    </Box>
                  ))}
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions style={{ padding: "6px 14px", borderTop: "1px solid #e0e0e0", minHeight: "auto" }}>
          {/* Footer removed - close button is in header */}
        </DialogActions>
      </Dialog>

      {showKOTPrint && printPayload && (
        <KOTPrint
          session={session}
          items={printPayload.items}
          kotNumber={printPayload.kotNumber}
          onPrintComplete={handlePrintComplete}
        />
      )}
    </>
  );
}

export default KOTDialog;
